import { BritishColumbiaObstetricalFilePageIdEnum } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFilePageIdEnum";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileType";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { OntarioObstetricalFilePageIdEnum } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFilePageIdEnum";
import { QuebecObstetricalFilePageIdEnum } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFilePageIdEnum";
import { FormExtensibleComponents } from "./FormExtendableComponentsEnum";

class FormDisplay {
    public selectedPageId: string;
    public extendedComponents: FormExtensibleComponents[] = [];

    constructor(fields?: Partial<FormDisplay>) {
        Object.assign(this, fields);
    }

    public static prepareForObstetricalFile(obstetricalFileModel: ObstetricalFileModel): FormDisplay {
        switch (obstetricalFileModel?.ObstetricalFileType) {
            case ObstetricalFileType.QuebecForm:
                return this.prepareForQuebecObstetricalFileForm(obstetricalFileModel);

            case ObstetricalFileType.OntarioForm:
                return this.prepareForOntarioObstetricalFileForm(obstetricalFileModel);

            case ObstetricalFileType.BritishColumbiaForm:
                return this.prepareForBritishColumbiaObstetricalFileForm(obstetricalFileModel);

            default:
                return null;
        }
    }

    private static prepareForQuebecObstetricalFileForm(obstetricalFileModel: ObstetricalFileModel): FormDisplay {
        const formDisplay = new FormDisplay()
            .extendSection(FormExtensibleComponents.Page1Header);

        if (obstetricalFileModel.ContributionType === ObstetricalFileContributionType.NewPregnancy)
            formDisplay
                .setSelectedPageId(QuebecObstetricalFilePageIdEnum.Page1.toString());

        else
            formDisplay
                .setSelectedPageId(QuebecObstetricalFilePageIdEnum.Page4.toString());

        return formDisplay;
    }

    public setSelectedPageId(selectedPageId: string): FormDisplay {
        this.selectedPageId = selectedPageId;
        return this;
    }

    public extend(): FormDisplay {
        this.extendSection(FormExtensibleComponents.Form);
        return this;
    }

    public collapse(): FormDisplay {
        this.collapseSection(FormExtensibleComponents.Form);
        return this;
    }

    public get isExtended(): boolean {
        return this.isSectionExtended(FormExtensibleComponents.Form);
    }

    public isSectionExtended(sectionNameEnum: FormExtensibleComponents): boolean {
        return this.extendedComponents.contains(sectionNameEnum);
    }

    public toggleSection(sectionNameEnum: FormExtensibleComponents): FormDisplay {
        if (this.isSectionExtended(sectionNameEnum))
            this.collapseSection(sectionNameEnum);
        else
            this.extendSection(sectionNameEnum);

        return this;
    }

    public extendSections(sectionsToExpand: FormExtensibleComponents[]): FormDisplay {
        sectionsToExpand.forEach(section => this.extendSection(section));
        return this;
    }

    public extendSection(sectionNameEnum: FormExtensibleComponents): FormDisplay {
        if (!this.isSectionExtended(sectionNameEnum))
            this.extendedComponents.push(sectionNameEnum);

        return this;
    }

    public collapseSection(sectionNameEnum: FormExtensibleComponents): FormDisplay {
        if (this.isSectionExtended(sectionNameEnum))
            this.extendedComponents.splice(this.extendedComponents.indexOf(sectionNameEnum), 1);

        return this;
    }

    private static prepareForOntarioObstetricalFileForm(obstetricalFileModel: ObstetricalFileModel): FormDisplay {
        const formDisplay = new FormDisplay();

        switch (obstetricalFileModel.ContributionType) {
            case ObstetricalFileContributionType.FollowUp:
                formDisplay
                    .setSelectedPageId(OntarioObstetricalFilePageIdEnum.Opr3.toString());
                break;
            case ObstetricalFileContributionType.Postnatal:
                formDisplay
                    .setSelectedPageId(OntarioObstetricalFilePageIdEnum.PostNatal.toString());
                break;
            default:
                formDisplay
                    .setSelectedPageId(OntarioObstetricalFilePageIdEnum.Opr1.toString());
        }

        return formDisplay;
    }

    private static prepareForBritishColumbiaObstetricalFileForm(obstetricalFileModel: ObstetricalFileModel): FormDisplay {
        const formDisplay = new FormDisplay();

        switch (obstetricalFileModel.ContributionType) {
            case ObstetricalFileContributionType.FollowUp:
                formDisplay
                    .setSelectedPageId(BritishColumbiaObstetricalFilePageIdEnum.Part2.toString());
                break;
            default:
                formDisplay
                    .setSelectedPageId(BritishColumbiaObstetricalFilePageIdEnum.Part1.toString());
        }

        return formDisplay;
    }
}

export { FormDisplay };
